<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { SmartPickerModal, type SmartPickerOption } from '@yuksalish/ui'
import { formatApiError } from '@yuksalish/api-client'
import { useFinanceApi } from '../composables/useFinanceApi'
import type { Option } from '../types/finance'

/**
 * Counterparty picker (customers and suppliers) backed by `GET /agents`.
 * Finance forms always pick a counterparty through this modal instead of a
 * native select, because the agent list is long and needs search + paging.
 */
const props = withDefaults(
  defineProps<{
    open: boolean
    title?: string
    searchPlaceholder?: string
    emptyMessage?: string
    loadingMessage?: string
    /** Restricts the list to one agent role; both roles are shown by default. */
    role?: 'customer' | 'supplier' | 'all'
    /** Id of the counterparty already set on the record, highlighted in the list. */
    selectedId?: string | number | null
  }>(),
  {
    title: '',
    searchPlaceholder: '',
    emptyMessage: '',
    loadingMessage: '',
    role: 'all',
    selectedId: null,
  },
)

const emit = defineEmits<{
  close: []
  select: [counterparty: Option]
}>()

const { t } = useI18n()
const { listCounterpartyOptions } = useFinanceApi()

const ROLE_LABEL_KEYS: Record<string, string> = {
  customer: 'finance.picker.roleCustomer',
  supplier: 'finance.picker.roleSupplier',
}

/** Rows per page; the agent list is paginated client-side. */
const PAGE_SIZE = 20

const searchValue = ref('')
const counterparties = ref<Option[]>([])
const loading = ref(false)
const error = ref('')
const page = ref(1)

function roleLabel(roles?: string[]): string {
  const role = (roles ?? []).find((value) => ROLE_LABEL_KEYS[value] !== undefined)
  if (!role) return ''
  const label = t(ROLE_LABEL_KEYS[role])
  return label && !String(label).startsWith('finance.') ? label : ''
}

const filtered = computed<SmartPickerOption[]>(() => {
  const search = searchValue.value.trim().toLowerCase()
  return counterparties.value
    .filter(
      (counterparty) =>
        props.role === 'all' || (counterparty.roles ?? []).includes(props.role),
    )
    .filter((counterparty) => !search || counterparty.name.toLowerCase().includes(search))
    .map((counterparty) => ({
      id: counterparty.id,
      title: counterparty.name,
      meta: roleLabel(counterparty.roles),
    }))
})

const totalPages = computed(() => Math.max(1, Math.ceil(filtered.value.length / PAGE_SIZE)))

const paged = computed(() => {
  const start = (page.value - 1) * PAGE_SIZE
  return filtered.value.slice(start, start + PAGE_SIZE)
})

const rangeLabel = computed(() => {
  const total = filtered.value.length
  const from = total === 0 ? 0 : (page.value - 1) * PAGE_SIZE + 1
  const to = Math.min(page.value * PAGE_SIZE, total)
  return t('finance.picker.pageRange', { from, to, total })
})

watch(searchValue, () => {
  page.value = 1
})

watch(totalPages, (value) => {
  if (page.value > value) page.value = value
})

function goToPage(next: number) {
  page.value = Math.min(Math.max(1, next), totalPages.value)
}

/** Opens the picker on the page that holds the already-selected counterparty. */
function jumpToSelection() {
  const selected = props.selectedId
  if (selected === null || selected === undefined) return
  const index = filtered.value.findIndex((option) => String(option.id) === String(selected))
  if (index >= 0) page.value = Math.floor(index / PAGE_SIZE) + 1
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    counterparties.value = await listCounterpartyOptions()
    jumpToSelection()
  } catch (cause) {
    error.value = formatApiError(cause)
    counterparties.value = []
  } finally {
    loading.value = false
  }
}

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return
    searchValue.value = ''
    page.value = 1
    void load()
  },
)

onMounted(() => {
  if (props.open) void load()
})
</script>

<template>
  <SmartPickerModal
    :open="open"
    :title="title || t('finance.picker.agentTitle')"
    :eyebrow="t('finance.picker.agentEyebrow')"
    :search-label="t('finance.picker.agentSearch')"
    :search-placeholder="searchPlaceholder || t('finance.picker.agentPlaceholder')"
    :search-value="searchValue"
    :loading="loading"
    :error-message="error"
    :empty-message="emptyMessage || t('finance.picker.agentEmpty')"
    :items="paged"
    :page="page"
    :total-pages="totalPages"
    :selected-id="selectedId"
    :page-label="rangeLabel"
    :close-aria-label="t('finance.picker.close')"
    :retry-label="t('finance.picker.retry')"
    :previous-label="t('finance.picker.previous')"
    :next-label="t('finance.picker.next')"
    :cancel-label="t('finance.picker.cancel')"
    @close="emit('close')"
    @retry="load"
    @previous="goToPage(page - 1)"
    @next="goToPage(page + 1)"
    @select="(option: SmartPickerOption) => {
      const match = counterparties.find((entry) => String(entry.id) === String(option.id))
      if (match) emit('select', match)
    }"
    @update:search-value="(value: string) => (searchValue = value)"
  >
    <template #loading>{{ loadingMessage || t('finance.picker.agentLoading') }}</template>
  </SmartPickerModal>
</template>
